import type { Career, CareerSkillRequirement, LearningResource, Skill, SkillGroupId } from "../data/schema";

export interface SkillAnswerMap {
  [skillId: string]: number;
}

export type MatchBand = "strong" | "good" | "developing" | "priority";

export interface SkillScore {
  skillId: string;
  importance: number;
  targetLevel: number;
  userLevel: number;
  achievementRatio: number;
  gap: number;
  priority: number;
}

export interface CategoryScore {
  groupId: SkillGroupId;
  percent: number;
  skillCount: number;
}

export interface CareerMatchResult {
  percent: number;
  band: MatchBand;
  skillScores: SkillScore[];
  categoryScores: CategoryScore[];
  strengths: SkillScore[];
  priorities: SkillScore[];
}

export function clampLevel(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(4, Math.max(0, Math.round(value)));
}

export function achievementRatio(userLevel: number, targetLevel: number): number {
  const user = clampLevel(userLevel);
  const target = clampLevel(targetLevel);
  if (target <= 0) return 1;
  return Math.min(user / target, 1);
}

export function skillGap(userLevel: number, targetLevel: number): number {
  return Math.max(clampLevel(targetLevel) - clampLevel(userLevel), 0);
}

export function matchBand(percent: number): MatchBand {
  if (percent >= 85) return "strong";
  if (percent >= 70) return "good";
  if (percent >= 50) return "developing";
  return "priority";
}

export function scoreRequirement(
  req: CareerSkillRequirement,
  answers: SkillAnswerMap,
): SkillScore {
  const userLevel = clampLevel(answers[req.skillId] ?? 0);
  const ratio = achievementRatio(userLevel, req.targetLevel);
  const gap = skillGap(userLevel, req.targetLevel);
  return {
    skillId: req.skillId,
    importance: req.importance,
    targetLevel: req.targetLevel,
    userLevel,
    achievementRatio: ratio,
    gap,
    priority: req.importance * gap,
  };
}

export function calculateCareerMatch(
  requirements: CareerSkillRequirement[],
  answers: SkillAnswerMap,
  skillsById: Map<string, Skill>,
): CareerMatchResult {
  const skillScores = requirements.map((req) => scoreRequirement(req, answers));
  const denominator = skillScores.reduce((sum, s) => sum + s.importance, 0);
  const numerator = skillScores.reduce((sum, s) => sum + s.achievementRatio * s.importance, 0);
  const percent = denominator === 0 ? 0 : (numerator / denominator) * 100;

  const grouped = new Map<SkillGroupId, SkillScore[]>();
  for (const score of skillScores) {
    const skill = skillsById.get(score.skillId);
    if (!skill) continue;
    const list = grouped.get(skill.groupId) ?? [];
    list.push(score);
    grouped.set(skill.groupId, list);
  }

  const categoryScores: CategoryScore[] = [...grouped.entries()].map(([groupId, list]) => {
    const den = list.reduce((sum, s) => sum + s.importance, 0);
    const num = list.reduce((sum, s) => sum + s.achievementRatio * s.importance, 0);
    return {
      groupId,
      percent: den === 0 ? 0 : (num / den) * 100,
      skillCount: list.length,
    };
  });

  const strengths = [...skillScores]
    .filter((s) => s.achievementRatio >= 0.75 && s.userLevel >= 2)
    .sort((a, b) => b.achievementRatio - a.achievementRatio || b.importance - a.importance)
    .slice(0, 5);

  const priorities = [...skillScores]
    .filter((s) => s.gap > 0)
    .sort((a, b) => b.priority - a.priority || b.importance - a.importance)
    .slice(0, 7);

  return {
    percent,
    band: matchBand(percent),
    skillScores,
    categoryScores,
    strengths,
    priorities,
  };
}

export function relatedCareersBySkillOverlap(
  selected: Career,
  all: Career[],
  limit = 4,
): Career[] {
  const selectedSkills = new Set(selected.requiredSkills.map((r) => r.skillId));
  return all
    .filter((c) => c.id !== selected.id && c.status === "published")
    .map((career) => {
      const overlap = career.requiredSkills.filter((r) => selectedSkills.has(r.skillId)).length;
      return { career, overlap };
    })
    .filter((x) => x.overlap > 0)
    .sort((a, b) => b.overlap - a.overlap)
    .slice(0, limit)
    .map((x) => x.career);
}

export function recommendLearningResources(
  prioritySkillIds: string[],
  resources: LearningResource[],
): LearningResource[] {
  const wanted = new Set(prioritySkillIds);
  const matched = resources.filter(
    (r) => r.status === "published" && r.skillsDeveloped.some((id) => wanted.has(id)),
  );
  const rest = resources.filter(
    (r) => r.status === "published" && !matched.includes(r) && r.availability === "coming-soon",
  );
  return [...matched, ...rest].slice(0, 6);
}

export function selectAssessmentSkills(career: Career, max = 25, min = 15): CareerSkillRequirement[] {
  const ranked = [...career.requiredSkills].sort((a, b) => b.importance - a.importance);
  const unique = new Map<string, CareerSkillRequirement>();
  for (const req of ranked) unique.set(req.skillId, req);
  let selected = [...unique.values()].slice(0, max);
  if (selected.length < min) selected = [...unique.values()];
  return selected;
}

export function developmentSequence(priorities: SkillScore[], skillsById: Map<string, Skill>): string[] {
  const names = priorities
    .slice(0, 3)
    .map((p) => skillsById.get(p.skillId)?.name)
    .filter((n): n is string => Boolean(n));
  const steps: string[] = [];
  if (names[0]) steps.push(`Strengthen ${names[0]}`);
  if (names[1]) steps.push(`Practice ${names[1]}`);
  if (names[2]) steps.push(`Explore ${names[2]}`);
  steps.push("Apply a research-based or laboratory BOOSTSCIENCE activity when available");
  steps.push("Connect the skills above with related career pathways in the Radar");
  return steps;
}
